import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalDelete, portalGet, portalPost } from '@/lib/portal/customerClient';
import { usePortalAuthStore } from '@/stores/portalAuth.store';

export interface PortalPaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  label: string | null;
  isDefault: boolean;
  isExpired: boolean;
  /** A prior charge on this card was declined — nudge the customer to update it. */
  needsAttention: boolean;
}

export interface PortalPaymentSetupIntent {
  clientSecret: string;
  publishableKey: string;
  stripeAccountId: string | null;
}

/**
 * Which add-card form to render. The portal cannot assume Stripe — Payabli is
 * the default processor for new tenants, and its save-a-card flow is an
 * embedded-component tokenize, not a SetupIntent. Without this the portal minted
 * a Stripe SetupIntent unconditionally and a Payabli tenant's customer got a
 * 400 instead of a card form.
 */
export interface PortalPaymentMethodsConfig {
  provider: 'STRIPE' | 'PAYABLI';
  canSaveCard: boolean;
  /** The Payabli payor identifier — the session's own contact id, server-derived. */
  contactId: string;
  acceptCard: boolean;
  acceptACH: boolean;
  payabli?: {
    entryPoint: string | null;
    publicToken: string | null;
    mode: 'TEST' | 'LIVE';
  };
}

const BASE = '/api/portal/payment-methods';

/** The signed-in contact's saved cards, default first. */
export function usePortalPaymentMethods() {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'payment-methods', token],
    queryFn: () => portalGet<PortalPaymentMethod[]>(BASE),
    enabled: Boolean(token),
    retry: false,
  });
}

/** The processor context the add-card dialog branches on. */
export function usePortalPaymentMethodsConfig(enabled = true) {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'payment-methods', 'config', token],
    queryFn: () => portalGet<PortalPaymentMethodsConfig>(`${BASE}/config`),
    enabled: Boolean(token) && enabled,
    retry: false,
  });
}

function useInvalidatePaymentMethods() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['portal', 'payment-methods'] });
    // The account read surfaces the default card; keep it in sync.
    void queryClient.invalidateQueries({ queryKey: ['portal', 'account'] });
  };
}

/** Create a Stripe SetupIntent so the browser can tokenize a card with Elements. */
export function usePortalCardSetupIntent() {
  return useMutation({
    mutationFn: () => portalPost<PortalPaymentSetupIntent>(`${BASE}/setup-intent`),
  });
}

/** Store the tokenized card (`pm_…`) Elements returned. The first card the
 *  customer saves becomes their default automatically, server-side — the client
 *  never asserts a default (a stale/failed list must not silently re-default). */
export function useAddPortalPaymentMethod() {
  const invalidate = useInvalidatePaymentMethods();
  return useMutation({
    mutationFn: (input: { providerPaymentMethodId: string }) =>
      portalPost<PortalPaymentMethod>(BASE, input),
    onSuccess: invalidate,
  });
}

/**
 * Payabli: exchange the embedded component's one-time token for a stored card.
 * The payor is the session contact server-side — the client sends only the
 * token and display hints, and the backend re-resolves the real rail/brand/last4
 * from the provider.
 */
export function useTokenizePortalPaymentMethod() {
  const invalidate = useInvalidatePaymentMethods();
  return useMutation({
    mutationFn: (input: {
      temporaryToken: string;
      type?: string;
      last4?: string;
      brand?: string | null;
      expMonth?: number | null;
      expYear?: number | null;
    }) => portalPost<PortalPaymentMethod>(`${BASE}/tokenize`, input),
    onSuccess: invalidate,
  });
}

export function useSetDefaultPortalPaymentMethod() {
  const invalidate = useInvalidatePaymentMethods();
  return useMutation({
    mutationFn: (id: string) =>
      portalPost<PortalPaymentMethod>(`${BASE}/${encodeURIComponent(id)}/default`),
    onSuccess: invalidate,
  });
}

export function useRemovePortalPaymentMethod() {
  const invalidate = useInvalidatePaymentMethods();
  return useMutation({
    mutationFn: (id: string) =>
      portalDelete<{ deleted: true }>(`${BASE}/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}
